import { z } from 'zod';
import { api } from './client';
import {
  cleanParams,
  itemSchema,
  moneySchema,
  paginatedSchema,
  type ListParams,
  type Paginated,
} from './schemas';

/** Query keys (same shape as queryKeys; kept here so the shared factory file stays untouched). */
export const shiftKeys = {
  all: ['shifts'] as const,
  me: ['shifts', 'me'] as const,
  sessions: (params: object) => ['shifts', 'sessions', params] as const,
  salesByShift: (range: object) => ['shifts', 'sales-by-shift', range] as const,
  alerts: ['shifts', 'alerts'] as const,
};

export const fixedShiftSchema = z.looseObject({
  key: z.string(),
  label: z.string(),
  start: z.string().nullish(),
  end: z.string().nullish(),
});
export type FixedShift = z.infer<typeof fixedShiftSchema>;

/** Session as returned by start/end/sessions/alerts. Figures are present wherever the server computed them. */
export const shiftSessionSchema = z.looseObject({
  id: z.number(),
  cashier_user_id: z.number(),
  cashier_name: z.string(),
  fixed_shift: z.string(),
  status: z.enum(['open', 'closed']),
  started_at: z.string(),
  ended_at: z.string().nullish(),
  cash_opening: moneySchema,
  cash_closing: moneySchema.nullish(),
  notes: z.string().nullish(),
  cash_sales: moneySchema.optional(),
  payouts: moneySchema.optional(),
  /** Omitted for cashiers while their shift is open (blind close). */
  expected_cash: moneySchema.optional(),
  /** null while open. */
  variance: moneySchema.nullish(),
});
export type ShiftSession = z.infer<typeof shiftSessionSchema>;

export const myShiftSchema = z.looseObject({
  enabled: z.boolean(),
  is_cashier: z.boolean(),
  open_session: shiftSessionSchema.nullish(),
  current_shift: fixedShiftSchema.nullish(),
  stale_session: z.boolean(),
  started_today: z.boolean(),
  last_session: z
    .looseObject({
      id: z.number(),
      fixed_shift: z.string(),
      status: z.string(),
      started_at: z.string().nullish(),
      ended_at: z.string().nullish(),
    })
    .nullish(),
  needs_start_prompt: z.boolean(),
});
export type MyShift = z.infer<typeof myShiftSchema>;

export const shiftAlertSchema = z.looseObject({
  severity: z.enum(['critical', 'warning']),
  reason: z.string(),
  session: shiftSessionSchema,
});
export type ShiftAlert = z.infer<typeof shiftAlertSchema>;

export const salesByShiftRowSchema = z.looseObject({
  key: z.string(),
  label: z.string(),
  start: z.string().nullish(),
  end: z.string().nullish(),
  bills: z.number(),
  sales: moneySchema,
});
export type SalesByShiftRow = z.infer<typeof salesByShiftRowSchema>;

export interface ShiftSessionListParams extends ListParams {
  from?: string;
  to?: string;
  status?: 'open' | 'closed' | '';
  cashier_user_id?: number | string | '';
}
export interface ShiftRange {
  from?: string;
  to?: string;
}

export async function getMyShift(): Promise<MyShift> {
  const { data } = await api.get('/shifts/me');
  return itemSchema(myShiftSchema).parse(data).data;
}

export async function startShift(input: { cash_opening: number; notes?: string }) {
  const { data } = await api.post('/shifts/start', cleanParams(input));
  return itemSchema(shiftSessionSchema).parse(data).data;
}

export async function endShift(input: { cash_closing: number; notes?: string }) {
  const { data } = await api.post('/shifts/end', cleanParams(input));
  return itemSchema(shiftSessionSchema).parse(data).data;
}

export async function listShiftSessions(
  params: ShiftSessionListParams = {},
): Promise<Paginated<ShiftSession>> {
  const { data } = await api.get('/shifts/sessions', { params: cleanParams(params) });
  return paginatedSchema(shiftSessionSchema).parse(data);
}

export async function getSalesByShift(range: ShiftRange = {}): Promise<SalesByShiftRow[]> {
  const { data } = await api.get('/shifts/sales-by-shift', { params: cleanParams(range) });
  return z.looseObject({ data: z.array(salesByShiftRowSchema) }).parse(data).data;
}

export async function getShiftAlerts(): Promise<ShiftAlert[]> {
  const { data } = await api.get('/shifts/alerts');
  return z.looseObject({ data: z.array(shiftAlertSchema) }).parse(data).data;
}
